import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AdminProvider, useAdmin } from './context/AdminContext';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Users from './pages/Users/Users';
import Profiles from './pages/Profiles/Profiles';
import IncompleteRegistrations from './pages/IncompleteRegistrations/IncompleteRegistrations';
import DeactivatedAccounts from './pages/DeactivatedAccounts/DeactivatedAccounts';
import OverflowTip from './components/OverflowTip';
import ProfileDetail from './pages/Profiles/ProfileDetail';
import ProfileEdit from './pages/Profiles/ProfileEdit';
import Services from './pages/Services/Services';
import VendorEdit from './pages/Services/VendorEdit';
import Plans from './pages/Plans/Plans';
import PlanRenewals from './pages/PlanRenewals/PlanRenewals';
import Backup from './pages/Backup/Backup';
import Bookings from './pages/Bookings/Bookings';
import Testimonials from './pages/Testimonials/Testimonials';
import SupportChat from './pages/SupportChat/SupportChat';
import Analytics from './pages/Analytics/Analytics';
import Notifications from './pages/Notifications/Notifications';
import Banners from './pages/Banners/Banners';
import Faqs from './pages/Faqs/Faqs';
import Blogs from './pages/Blogs/Blogs';
import ServiceCards from './pages/ServiceCards/ServiceCards';
import AppLinks from './pages/AppLinks/AppLinks';
import HomeStats from './pages/HomeStats/HomeStats';
import ProviderDetail from './pages/Users/ProviderDetail';
import ServiceCategories from './pages/ServiceCategories/ServiceCategories';
import Messages from './pages/Messages/Messages';
import AdminAccess from './pages/AdminAccess/AdminAccess';
import ProfileOptions from './pages/ProfileOptions/ProfileOptions';
import AccountPasswords from './pages/AccountPasswords/AccountPasswords';
import PasswordVault from './pages/PasswordVault/PasswordVault';
import MyAccount from './pages/MyAccount/MyAccount';
import Offline from './pages/Offline/Offline';
import ThemeSettings from './components/ThemeSettings';
import GlobalLoader from './components/GlobalLoader';
import CustomToast from './components/CustomToast';
import ConnectivityRedirect from './components/ConnectivityRedirect';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { admin, loading } = useAdmin();
  // The initial /auth/me check already runs through the shared API
  // instance, so GlobalLoader's overlay covers this wait on its own —
  // rendering nothing here avoids stacking a second loader on top of it.
  if (loading) return null;
  return admin ? <>{children}<ThemeSettings withSubscription /></> : <Navigate to="/login" />;
};

// old bookmarks of /services/vendors/<id> (and /edit) open the provider's single page
const RedirectToProvider = ({ edit = false }) => {
  const { id } = useParams();
  return <Navigate replace to={`/service-providers/${id}${edit ? '/edit' : ''}`} />;
};

function App() {
  return (
    <AdminProvider>
      <Router>
        <ConnectivityRedirect />
        <Toaster position="top-right" gutter={10}>
          {t => <CustomToast t={t} />}
        </Toaster>
        <GlobalLoader />
        <OverflowTip />
        <Routes>
          <Route path="/login" element={<><Login /><ThemeSettings /></>} />
          <Route path="/offline" element={<><Offline /><ThemeSettings /></>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><Users key="members" scope="member" /></ProtectedRoute>} />
          <Route path="/service-providers" element={<ProtectedRoute><Users key="providers" scope="service" /></ProtectedRoute>} />
          <Route path="/service-providers/:id" element={<ProtectedRoute><ProviderDetail /></ProtectedRoute>} />
          <Route path="/profiles" element={<ProtectedRoute><Profiles /></ProtectedRoute>} />
          <Route path="/incomplete-registrations" element={<ProtectedRoute><IncompleteRegistrations /></ProtectedRoute>} />
          <Route path="/deactivated-accounts" element={<ProtectedRoute><DeactivatedAccounts /></ProtectedRoute>} />
          <Route path="/profiles/:id" element={<ProtectedRoute><ProfileDetail /></ProtectedRoute>} />
          <Route path="/profiles/:id/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
          <Route path="/services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
          {/* a service provider has one page and one edit form (under /service-providers); the old addresses forward to them */}
          <Route path="/service-providers/:id/edit" element={<ProtectedRoute><VendorEdit /></ProtectedRoute>} />
          <Route path="/services/vendors/:id" element={<RedirectToProvider />} />
          <Route path="/services/vendors/:id/edit" element={<RedirectToProvider edit />} />
          <Route path="/profile-options" element={<ProtectedRoute><ProfileOptions /></ProtectedRoute>} />
          <Route path="/account-passwords" element={<ProtectedRoute><AccountPasswords /></ProtectedRoute>} />
          <Route path="/password-vault" element={<ProtectedRoute><PasswordVault /></ProtectedRoute>} />
          <Route path="/my-account" element={<ProtectedRoute><MyAccount /></ProtectedRoute>} />
          <Route path="/plans"element={<ProtectedRoute><Plans /></ProtectedRoute>} />
          <Route path="/plan-renewals" element={<ProtectedRoute><PlanRenewals /></ProtectedRoute>} />
          <Route path="/backup" element={<ProtectedRoute><Backup /></ProtectedRoute>} />
          <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
          <Route path="/testimonials" element={<ProtectedRoute><Testimonials /></ProtectedRoute>} />
          <Route path="/support-chat" element={<ProtectedRoute><SupportChat /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/banners" element={<ProtectedRoute><Banners /></ProtectedRoute>} />
          <Route path="/faqs" element={<ProtectedRoute><Faqs /></ProtectedRoute>} />
          <Route path="/blogs" element={<ProtectedRoute><Blogs /></ProtectedRoute>} />
          <Route path="/service-categories" element={<ProtectedRoute><ServiceCategories /></ProtectedRoute>} />
          <Route path="/service-cards" element={<ProtectedRoute><ServiceCards /></ProtectedRoute>} />
          <Route path="/app-links" element={<ProtectedRoute><AppLinks /></ProtectedRoute>} />
          <Route path="/home-stats" element={<ProtectedRoute><HomeStats /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/admin-access" element={<ProtectedRoute><AdminAccess /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Router>
    </AdminProvider>
  );
}

export default App;
